import { useState, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import debounce from 'hero-utils/debounce';
import useHasValueChanged from 'react-hooks/useHasValueChanged';
import usePrevious from 'react-hooks/usePrevious';
import { useShouldValidate } from './useShouldValidate';
import processChildren from './processChildren';
import filterSuggestions from './filterSuggestions';
import useFormValueUpdate from './useFormValueUpdate';
import useOnValueSetCb from './useOnValueSetCb';
import useChangeEvents from './useChangeEvents';
import noOpHandleFetchSuggestions from './noOpHandleFetchSuggestions';
const useComboboxState = ({ name, maxLength, handleFetchSuggestions, suggestions, onSelection, onChange, isToggled, setOff, setOn, preventSuggestionFilter, onlyValidateWithError, showCreateNewOption }) => {
    const [enteredValue, setEnteredValue] = useState('');
    const [suggestedValue, setSuggestedValue] = useState(enteredValue);
    const [isNavigated, setIsNavigated] = useState(false);
    const { setValue, formState: { errors: { [name]: fieldErrors } } } = useFormContext();
    const shouldValidate = useShouldValidate({ onlyValidateWithError, fieldErrors });
    // eslint suggestion would complicate testing
    /* eslint-disable react-hooks/exhaustive-deps*/
    const debouncedHandleFetchSuggestions = useCallback(debounce(handleFetchSuggestions || noOpHandleFetchSuggestions, 275), [handleFetchSuggestions]);
    /* eslint-enable react-hooks/exhaustive-deps*/
    const hasEnteredValueChanged = useHasValueChanged(enteredValue);
    const previousEnteredValue = usePrevious(enteredValue);
    const filteredSuggestions = preventSuggestionFilter
        ? suggestions
        : filterSuggestions(suggestions, enteredValue);
    const suggestionOptions = processChildren({
        suggestions: filteredSuggestions,
        name,
        suggestedValue,
        setSuggestedValue,
        setEnteredValue,
        onSelection,
        setIsNavigated,
        setOff
    });
    const { handleKeyDown, handleChange } = useChangeEvents({
        suggestions: filteredSuggestions,
        suggestedValue,
        enteredValue,
        setSuggestedValue,
        setEnteredValue,
        onSelection,
        setIsNavigated,
        isNavigated,
        isToggled,
        setOn,
        setOff
    });
    useFormValueUpdate({
        name,
        enteredValue,
        previousEnteredValue,
        hasEnteredValueChanged,
        handleFetchSuggestions: debouncedHandleFetchSuggestions,
        setValue,
        maxLength,
        shouldValidate
    });
    useOnValueSetCb({ onChange, isNavigated, hasEnteredValueChanged, enteredValue });
    const showCreateNewLabel = useMemo(() => {
        const filSuggestions = suggestions.filter((suggestion) => suggestion.toLocaleLowerCase().includes(enteredValue.toLocaleLowerCase()));
        return showCreateNewOption && filSuggestions.length === 0 && enteredValue.length > 2;
    }, [showCreateNewOption, suggestions, enteredValue]);
    return {
        setEnteredValue,
        suggestedValue,
        setSuggestedValue,
        setIsNavigated,
        handleKeyDown,
        handleChange,
        hasSuggestions: !!filteredSuggestions.length,
        filteredSuggestions,
        suggestionOptions,
        showCreateNewLabel
    };
};
export default useComboboxState;
//# sourceMappingURL=useComboboxState.js.map